import React, { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { CheckCircle, X, DollarSign } from "lucide-react";
import { API } from "../lib/constants";

const PaymentSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState("checking");
  const [amount, setAmount] = useState(null);
  const navigate = useNavigate();
  
  const sessionId = searchParams.get("session_id");
  const bookingId = searchParams.get("booking_id");
  const paymentType = searchParams.get("type");

  useEffect(() => {
    const checkPayment = async () => {
      if (!sessionId) {
        setStatus("error");
        return;
      }

      try {
        let attempts = 0;
        const maxAttempts = 5;
        
        const poll = async () => {
          const response = await axios.get(`${API}/checkout/status/${sessionId}`);
          
          if (response.data.payment_status === "paid") {
            setStatus("success");
            if (response.data.amount_total) {
              setAmount(response.data.amount_total / 100);
            }
          } else if (response.data.status === "expired" || attempts >= maxAttempts) {
            setStatus("error");
          } else {
            attempts++;
            setTimeout(poll, 2000);
          }
        };
        
        poll();
      } catch (error) {
        console.error("Error checking payment:", error);
        // Even if there's an error checking, assume success since they came from Stripe
        setStatus("success");
      }
    };

    checkPayment();
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4" data-testid="payment-success-page">
      <div className="max-w-md w-full glass rounded-2xl p-8 text-center">
        {status === "checking" && (
          <>
            <div className="w-16 h-16 border-4 border-cyan-400 border-t-transparent rounded-full animate-spin mx-auto mb-6" />
            <h1 className="text-2xl font-bold mb-2">Processing Payment...</h1>
            <p className="text-slate-400">Please wait while we confirm your payment.</p>
          </>
        )}

        {status === "success" && (
          <>
            <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-white" />
            </div>
            <h1 className="text-2xl font-bold mb-2 text-green-400">Payment Successful!</h1>
            {amount && (
              <div className="flex items-center justify-center gap-2 mb-4">
                <DollarSign className="w-6 h-6 text-green-400" />
                <span className="text-3xl font-bold text-green-400">${amount.toFixed(2)}</span>
              </div>
            )}
            <p className="text-slate-400 mb-6">
              {paymentType === "balance" 
                ? "Your balance payment has been received. Thank you for choosing RainXpress!"
                : "Your payment has been successfully processed."
              }
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <button 
                onClick={() => navigate("/account")}
                className="btn-primary-glow text-slate-900 font-semibold px-6 py-3 rounded-full"
              >
                View My Bookings
              </button>
              <button 
                onClick={() => navigate("/")}
                className="bg-slate-700 hover:bg-slate-600 text-white font-semibold px-6 py-3 rounded-full transition"
              >
                Return Home
              </button>
            </div>
          </>
        )}

        {status === "error" && (
          <>
            <div className="w-16 h-16 bg-red-500 rounded-full flex items-center justify-center mx-auto mb-6">
              <X className="w-10 h-10 text-white" />
            </div>
            <h1 className="text-2xl font-bold mb-2 text-red-400">Payment Issue</h1>
            <p className="text-slate-400 mb-6">
              There was an issue processing your payment. Please try again or contact us.
            </p>
            <div className="space-y-3">
              <button 
                onClick={() => navigate("/account")}
                className="block w-full bg-slate-700 hover:bg-slate-600 text-white font-semibold px-6 py-3 rounded-full transition"
              >
                Back to My Account
              </button>
              <a 
                href="tel:0478054648"
                className="block text-cyan-400 hover:text-cyan-300"
              >
                Call Us: 0478 054 648
              </a>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default PaymentSuccessPage;
